import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  CBS_WORKFLOW, 
  CAP_HIGH_WORKFLOW, 
  CAP_MEDIUM_WORKFLOW,
  CAP_OPTIONS,
  CAP_CLASSIFICATION,
  canUserAccessStep,
  getStepById,
  ROLES,
  APPROVAL_STATUS
} from '../config/workflowConfig';

function CustomerCaseManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customers, addCase, updateCase, deleteCase, cases = [], indicators = [] } = useData();
  const { user, filterCustomersByBranch } = useAuth();
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentCase, setCurrentCase] = useState(null);
  const [currentWorkflow, setCurrentWorkflow] = useState('cbs');
  const [currentStep, setCurrentStep] = useState(CBS_WORKFLOW.steps[0]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [indicatorInputs, setIndicatorInputs] = useState([{ indicatorId: '', value: '', notes: '' }]);
  const [documentNote, setDocumentNote] = useState('');
  const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
  const [isDraggingCaseDocs, setIsDraggingCaseDocs] = useState(false);
  const [stepAssignments, setStepAssignments] = useState({});
  const [accessControl, setAccessControl] = useState({
    branches: ['all'],
    groups: ['all'],
    users: ['all']
  });

  const branchOptions = [
    { value: 'all', label: '🌐 Tất cả chi nhánh' },
    { value: 'CN Hà Nội', label: 'CN Hà Nội' },
    { value: 'CN TP HCM', label: 'CN TP HCM' },
    { value: 'CN Đà Nẵng', label: 'CN Đà Nẵng' },
    { value: 'CN Hải Phòng', label: 'CN Hải Phòng' },
    { value: 'CN Cần Thơ', label: 'CN Cần Thơ' },
    { value: 'CN Nha Trang – Khánh Hòa', label: 'CN Nha Trang – Khánh Hòa' }
  ];

  const groupOptions = [
    { value: 'all', label: '🌐 Tất cả nhóm người dùng' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Analyst', label: 'Analyst' },
    { value: 'Viewer', label: 'Viewer' }
  ];

  const getRoleNameFromIds = (roleIds = []) => {
    if (roleIds.includes(1)) return 'Admin';
    if (roleIds.includes(2)) return 'Manager';
    if (roleIds.includes(3)) return 'Analyst';
    return 'Viewer';
  };

  const userDirectory = useMemo(() => {
    const fallbackUsers = [
      {
        id: 1,
        name: 'Nguyễn Văn A',
        email: 'admin@ey.com',
        group: 'Admin',
        managedBranches: ['all']
      },
      {
        id: 2,
        name: 'Trần Thị B',
        email: 'manager@ey.com',
        group: 'Manager',
        managedBranches: ['CN TP HCM', 'CN Đà Nẵng']
      },
      {
        id: 3,
        name: 'Lê Văn C',
        email: 'analyst@ey.com',
        group: 'Analyst',
        managedBranches: ['CN Hà Nội']
      }
    ];

    try {
      const savedUsers = localStorage.getItem('ews_users');
      if (!savedUsers) return fallbackUsers;
      const parsedUsers = JSON.parse(savedUsers);
      if (!Array.isArray(parsedUsers) || parsedUsers.length === 0) return fallbackUsers;

      return parsedUsers.map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        group: u.role || getRoleNameFromIds(u.roleIds || []),
        managedBranches: u.managedBranches || u.branches || ['all']
      }));
    } catch (error) {
      console.error('Failed to parse ews_users from localStorage:', error);
      return fallbackUsers;
    }
  }, []);
  
  // Get active indicators for dropdown
  const activeIndicators = indicators.filter(ind => ind.status === 'active');
  
  // Debug: Log cases when they change
  useEffect(() => {
    console.log('📦 CustomerCaseManagement - Cases updated:', {
      total: cases.length,
      cases: cases.map(c => ({ id: c.id, customerId: c.customerId, isManual: c.isManual, isHidden: c.isHidden }))
    });
  }, [cases]);
  
  // Load customer from navigation state
  useEffect(() => {
    if (location.state?.customer) {
      handleSelectCustomer(location.state.customer);
    }
  }, [location.state]);
  
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    
    // Check if case already exists
    const existingCase = cases.find(c => c.customerId === customer.id && c.status !== 'closed');
    
    if (existingCase) {
      setCurrentCase(existingCase);
      setCurrentWorkflow(existingCase.currentWorkflow);
      setStepAssignments(existingCase.stepAssignments || {});
      setAccessControl(existingCase.accessControl || { branches: ['all'], groups: ['all'], users: ['all'] });
      const workflow = existingCase.currentWorkflow === 'cbs' ? CBS_WORKFLOW : 
                      existingCase.riskLevel === 'high' ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;
      const step = workflow.steps.find(s => s.id === existingCase.currentStepId);
      setCurrentStep(step || workflow.steps[0]);
    } else {
      // Create new case - automatically move to step 2 since step 1 (select customer) is done
      const newCase = {
        id: `CASE-${Date.now()}`,
        customerId: customer.id,
        customerName: customer.name,
        riskLevel: customer.risk,
        currentWorkflow: 'cbs',
        currentStepId: 'cbs_4', // Start at step 2 (Điều tra khách hàng CBS)
        status: 'in_progress',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: {
          id: user?.id || null,
          name: user?.name || user?.username || 'Current User',
          email: user?.email || '',
          group: user?.role || getRoleNameFromIds(user?.roleIds || [])
        },
        accessControl: {
          branches: ['all'],
          groups: ['all'],
          users: ['all']
        },
        stepAssignments: {
          cbs_1: String(user?.id || 'creator')
        },
        history: [
          {
            stepId: 'cbs_1',
            action: 'select_customer',
            timestamp: new Date().toISOString(),
            user: user?.username || 'Current User',
            notes: `Đã chọn khách hàng ${customer.name}`,
            status: 'completed'
          }
        ],
        data: {}
      };
      
      addCase(newCase);
      setCurrentCase(newCase);
      setStepAssignments(newCase.stepAssignments || {});
      setAccessControl(newCase.accessControl);
      setCurrentWorkflow('cbs');
      setCurrentStep(CBS_WORKFLOW.steps.find(s => s.id === 'cbs_4')); // Start at Điều tra khách hàng CBS
    }
  };
  
  // Select case instead of customer
  const handleSelectCase = (selectedCase) => {
    setCurrentCase(selectedCase);
    setCurrentWorkflow(selectedCase.currentWorkflow);
    setStepAssignments(selectedCase.stepAssignments || {});
    setAccessControl(selectedCase.accessControl || { branches: ['all'], groups: ['all'], users: ['all'] });
    
    // Get customer info
    const customer = customers.find(c => c.id === selectedCase.customerId);
    setSelectedCustomer(customer);
    
    // Get current step
    const workflow = selectedCase.currentWorkflow === 'cbs' ? CBS_WORKFLOW : 
                    selectedCase.riskLevel === 'high' || selectedCase.riskLevel === 'Cao'
                    ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;
    const step = workflow.steps.find(s => s.id === selectedCase.currentStepId);
    setCurrentStep(step || workflow.steps[0]);
  };
  
  const openModal = (type) => {
    setModalType(type);
    
    // Preload previously saved data for current step so users can review/edit later
    const existingHistory = currentCase?.history.find(h => h.stepId === currentStep?.id);
    if (existingHistory?.data) {
      setFormData({
        ...existingHistory.data,
        notes: existingHistory.data.notes || existingHistory.notes || '',
        comment: existingHistory.data.comment || existingHistory.notes || ''
      });
      setSelectedFiles(existingHistory.data.files || []);
      if (existingHistory.data.indicators && existingHistory.data.indicators.length > 0) {
        setIndicatorInputs(existingHistory.data.indicators);
      } else {
        setIndicatorInputs([{ indicatorId: '', value: '', notes: '' }]);
      }
    } else {
      setFormData({});
      setSelectedFiles([]);
      setIndicatorInputs([{ indicatorId: '', value: '', notes: '' }]); // Reset indicator inputs
    }
    
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setFormData({});
    setSelectedFiles([]);
    setIndicatorInputs([{ indicatorId: '', value: '', notes: '' }]);
  };
  
  // View step data detail
  const [viewingStepData, setViewingStepData] = useState(null);
  
  const viewStepData = (stepId) => {
    const historyItem = currentCase?.history.find(h => h.stepId === stepId);
    setViewingStepData(historyItem);
  };
  
  const closeViewStepData = () => {
    setViewingStepData(null);
  };

  const handleSelectWithAll = (selectedOptions, key) => {
    const values = (selectedOptions || []).map(opt => opt.value);
    if (values.includes('all')) {
      setAccessControl(prev => ({ ...prev, [key]: ['all'] }));
      if (key === 'branches' || key === 'groups') {
        setAccessControl(prev => ({ ...prev, users: ['all'] }));
      }
      return;
    }
    setAccessControl(prev => ({ ...prev, [key]: values.length > 0 ? values : ['all'] }));
  };

  const filteredAssignableUsers = userDirectory.filter((u) => {
    const branches = accessControl.branches || ['all'];
    const groups = accessControl.groups || ['all'];

    const branchMatch = branches.includes('all') ||
      u.managedBranches.includes('all') ||
      u.managedBranches.some(b => branches.includes(b));

    const groupMatch = groups.includes('all') || groups.includes(u.group);

    return branchMatch && groupMatch;
  });

  const executionAssignableUsers = filteredAssignableUsers.filter((u) => {
    const allowedUsers = accessControl.users || ['all'];
    return allowedUsers.includes('all') || allowedUsers.includes(String(u.id));
  });

  const stepExecutionOptions = [
    { value: 'all', label: '🌐 Tất cả user được phân quyền' },
    ...executionAssignableUsers.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email}) • ${u.group}`
    }))
  ];

  const userOptions = [
    { value: 'all', label: '🌐 Tất cả người dùng phù hợp' },
    ...filteredAssignableUsers.map(u => ({
      value: String(u.id),
      label: `${u.name} (${u.email}) • ${u.group}`
    }))
  ];

  const getCurrentUserGroup = () => {
    if (!user) return 'Unknown';
    if (user.role) return user.role;
    if (user.roleIds?.includes(1)) return 'Admin';
    if (user.roleIds?.includes(2)) return 'Manager';
    if (user.roleIds?.includes(3)) return 'Analyst';
    return 'Viewer';
  };

  const canCurrentUserViewCaseByAccessControl = (caseItem) => {
    const config = caseItem.accessControl || { branches: ['all'], groups: ['all'], users: ['all'] };
    const currentUserBranches = user?.managedBranches || [];
    const currentUserGroup = getCurrentUserGroup();

    const branchMatch = (config.branches || ['all']).includes('all') ||
      currentUserBranches.includes('all') ||
      currentUserBranches.some(b => (config.branches || []).includes(b));

    const groupMatch = (config.groups || ['all']).includes('all') ||
      (config.groups || []).includes(currentUserGroup);

    const userMatch = (config.users || ['all']).includes('all') ||
      (config.users || []).includes(String(user?.id));

    return branchMatch && groupMatch && userMatch;
  };

  const saveCaseAccessControl = () => {
    if (!currentCase) return;

    const previousAccessControl = currentCase.accessControl || { branches: ['all'], groups: ['all'], users: ['all'] };

    const allowedIds = new Set(executionAssignableUsers.map((u) => String(u.id)));
    const normalizedAssignments = Object.entries(stepAssignments || {}).reduce((acc, [stepId, assignedUser]) => {
      if (stepId === 'cbs_1') {
        acc[stepId] = String(currentCase.createdBy?.id || user?.id || 'creator');
        return acc;
      }
      if (assignedUser === 'all' || !assignedUser || allowedIds.has(String(assignedUser))) {
        acc[stepId] = assignedUser || 'all';
      } else {
        acc[stepId] = 'all';
      }
      return acc;
    }, {});

    const updatedCase = {
      ...currentCase,
      updatedAt: new Date().toISOString(),
      accessControl,
      stepAssignments: normalizedAssignments,
      history: [
        ...currentCase.history,
        {
          stepId: 'case_access_control',
          action: 'update_access_control',
          timestamp: new Date().toISOString(),
          user: user?.username || user?.name || 'Current User',
          notes: `Cập nhật phân quyền (CN: ${(previousAccessControl.branches || ['all']).join(', ')} → ${(accessControl.branches || ['all']).join(', ')} | Nhóm: ${(previousAccessControl.groups || ['all']).join(', ')} → ${(accessControl.groups || ['all']).join(', ')} | User: ${(previousAccessControl.users || ['all']).join(', ')} → ${(accessControl.users || ['all']).join(', ')})`,
          data: {
            before: previousAccessControl,
            after: accessControl,
            stepAssignments: normalizedAssignments
          },
          status: 'completed'
        }
      ]
    };

    updateCase(updatedCase);
    setCurrentCase(updatedCase);
    setStepAssignments(normalizedAssignments);
    alert('✅ Đã lưu cấu hình phân quyền case');
  };

  const getStepAssignedUserValue = (stepId, caseItem = currentCase) => {
    if (!caseItem) return 'all';
    if (stepId === 'cbs_1') {
      return String(caseItem.createdBy?.id || user?.id || 'creator');
    }
    return caseItem.stepAssignments?.[stepId] || stepAssignments?.[stepId] || 'all';
  };

  const getStepAssignedUserLabel = (stepId, caseItem = currentCase) => {
    if (!caseItem) return 'All user được phân quyền';
    if (stepId === 'cbs_1') {
      return caseItem.createdBy?.name || 'User tạo case';
    }
    const assignedValue = getStepAssignedUserValue(stepId, caseItem);
    if (assignedValue === 'all') return 'All user được phân quyền';
    const assignedUser = userDirectory.find((u) => String(u.id) === String(assignedValue));
    return assignedUser ? assignedUser.name : `User #${assignedValue}`;
  };

  const canCurrentUserExecuteStep = (stepId, caseItem = currentCase) => {
    if (!caseItem) return false;
    if (stepId === 'cbs_1') {
      const creatorId = caseItem.createdBy?.id;
      if (creatorId === undefined || creatorId === null) return true;
      return String(user?.id) === String(creatorId);
    }

    const assignedValue = getStepAssignedUserValue(stepId, caseItem);
    if (assignedValue === 'all') return true;
    return String(user?.id) === String(assignedValue);
  };

  const updateStepAssignment = (stepId, selectedUserId) => {
    if (!currentCase || !stepId) return;

    const normalizedUserId = stepId === 'cbs_1'
      ? String(currentCase.createdBy?.id || user?.id || 'creator')
      : (selectedUserId || 'all');

    const currentValue = getStepAssignedUserValue(stepId, currentCase);
    if (String(currentValue) === String(normalizedUserId)) return;

    const nextAssignments = {
      ...(currentCase.stepAssignments || {}),
      [stepId]: normalizedUserId
    };

    const updatedCase = {
      ...currentCase,
      updatedAt: new Date().toISOString(),
      stepAssignments: nextAssignments,
      history: [
        ...currentCase.history,
        {
          stepId: `assign_${stepId}`,
          action: 'assign_step_executor',
          timestamp: new Date().toISOString(),
          user: user?.username || user?.name || 'Current User',
          notes: `Phân công bước ${stepId}: ${currentValue === 'all' ? 'all' : currentValue} → ${normalizedUserId === 'all' ? 'all' : normalizedUserId}`,
          data: {
            stepId,
            before: currentValue,
            after: normalizedUserId
          },
          status: 'completed'
        }
      ]
    };

    updateCase(updatedCase);
    setCurrentCase(updatedCase);
    setStepAssignments(nextAssignments);
  };
  
  // Edit step data
  const editStepData = (stepId) => {
    const historyItem = currentCase?.history.find(h => h.stepId === stepId);
    const workflow = currentWorkflow === 'cbs' ? CBS_WORKFLOW : 
                     currentCase?.riskLevel === 'high' || currentCase?.riskLevel === 'Cao' 
                     ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;
    const step = workflow.steps.find(s => s.id === stepId);
    
    if (historyItem && step) {
      setCurrentStep(step);
      setModalType(step.action);
      
      // Load existing data
      if (step.action === 'input_data' && historyItem.data?.indicators) {
        setIndicatorInputs(historyItem.data.indicators);
      } else {
        setFormData(historyItem.data || {});
        setSelectedFiles(historyItem.data?.files || []);
      }
      
      setShowModal(true);
    }
  };
  
  // Indicator inputs handlers
  const addIndicatorInput = () => {
    setIndicatorInputs([...indicatorInputs, { indicatorId: '', value: '', notes: '' }]);
  };
  
  const removeIndicatorInput = (index) => {
    if (indicatorInputs.length > 1) {
      setIndicatorInputs(indicatorInputs.filter((_, i) => i !== index));
    }
  };
  
  const updateIndicatorInput = (index, field, value) => {
    const updated = [...indicatorInputs];
    updated[index][field] = value;
    setIndicatorInputs(updated);
  };
  
  const appendSelectedFiles = (files = []) => {
    const fileObjects = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      originalFile: file
    }));

    setSelectedFiles(prev => [...prev, ...fileObjects]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    appendSelectedFiles(files);
    e.target.value = '';
  };

  const handleAttachmentDrop = (e) => {
    e.preventDefault();
    setIsDraggingAttachment(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      appendSelectedFiles(files);
    }
  };

  const downloadAttachment = (file) => {
    let blob;
    if (file?.originalFile) {
      blob = file.originalFile;
    } else {
      const fallback = `File: ${file?.name || 'unknown'}\nDung lượng: ${file?.size || 0} bytes\nLoại: ${file?.type || 'N/A'}\nGhi chú: File này được lưu dạng metadata.`;
      blob = new Blob([fallback], { type: 'text/plain;charset=utf-8' });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file?.name || 'attachment.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const formatFileSize = (size = 0) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };
  
  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleCaseDocumentUpload = (e) => {
    if (!currentCase) return;

    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const uploadedAt = new Date().toISOString();
    const uploadedBy = user?.username || 'Current User';
    const note = documentNote.trim();

    const newDocuments = files.map(file => ({
      id: `DOC-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: uploadedAt,
      uploadedBy,
      note,
      originalFile: file
    }));

    const existingDocuments = currentCase.data?.documents || [];
    const updatedCase = {
      ...currentCase,
      updatedAt: uploadedAt,
      data: {
        ...currentCase.data,
        documents: [...existingDocuments, ...newDocuments]
      },
      history: [
        ...currentCase.history,
        {
          stepId: 'case_documents',
          action: 'upload_documents',
          timestamp: uploadedAt,
          user: uploadedBy,
          notes: note || `Upload ${files.length} tài liệu`,
          data: { files: newDocuments, notes: note },
          status: 'completed'
        }
      ]
    };

    updateCase(updatedCase);
    setCurrentCase(updatedCase);
    setDocumentNote('');
    e.target.value = '';
  };

  const handleCaseDocumentDrop = (e) => {
    e.preventDefault();
    setIsDraggingCaseDocs(false);

    if (!currentCase) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;

    const uploadedAt = new Date().toISOString();
    const uploadedBy = user?.username || 'Current User';
    const note = documentNote.trim();
    const newDocuments = files.map(file => ({
      id: `DOC-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: uploadedAt,
      uploadedBy,
      note,
      originalFile: file
    }));

    const existingDocuments = currentCase.data?.documents || [];
    const updatedCase = {
      ...currentCase,
      updatedAt: uploadedAt,
      data: {
        ...currentCase.data,
        documents: [...existingDocuments, ...newDocuments]
      },
      history: [
        ...currentCase.history,
        {
          stepId: 'case_documents',
          action: 'upload_documents',
          timestamp: uploadedAt,
          user: uploadedBy,
          notes: note || `Upload ${files.length} tài liệu`,
          data: { files: newDocuments, notes: note },
          status: 'completed'
        }
      ]
    };

    updateCase(updatedCase);
    setCurrentCase(updatedCase);
    setDocumentNote('');
  };

  const removeCaseDocument = (docId) => {
    if (!currentCase) return;
    if (!window.confirm('Bạn có chắc muốn xóa tài liệu này?')) return;

    const updatedDocuments = (currentCase.data?.documents || []).filter(doc => doc.id !== docId);
    const updatedCase = {
      ...currentCase,
      updatedAt: new Date().toISOString(),
      data: {
        ...currentCase.data,
        documents: updatedDocuments
      }
    };

    updateCase(updatedCase);
    setCurrentCase(updatedCase);
  };
  
  const handleSubmitAction = (e) => {
    e.preventDefault();
    
    if (!currentCase || !currentStep) return;
    
    // For input_data action, validate indicators
    if (currentStep.action === 'input_data') {
      const validIndicators = indicatorInputs.filter(inp => inp.indicatorId && inp.value);
      if (validIndicators.length === 0) {
        alert('Vui lòng nhập ít nhất một chỉ tiêu!');
        return;
      }
    }
    
    // Prepare data to save
    const dataToSave = currentStep.action === 'input_data' 
      ? {
          indicators: indicatorInputs.filter(inp => inp.indicatorId && inp.value),
          notes: formData.notes || '',
          files: selectedFiles
        }
      : { ...formData, files: selectedFiles };
    
    // Check if editing existing step or adding new
    const existingHistoryIndex = currentCase.history.findIndex(h => h.stepId === currentStep.id);
    const isEditing = existingHistoryIndex >= 0;
    
    let updatedHistory;
    if (isEditing) {
      // Update existing history item
      updatedHistory = [...currentCase.history];
      updatedHistory[existingHistoryIndex] = {
        ...updatedHistory[existingHistoryIndex],
        timestamp: new Date().toISOString(),
        user: user?.username || 'Current User',
        notes: formData.notes || formData.comment || '',
        data: dataToSave,
        status: 'completed',
        approvalStatus: formData.decision || APPROVAL_STATUS.APPROVED
      };
    } else {
      // Add new history item
      updatedHistory = [
        ...currentCase.history,
        {
          stepId: currentStep.id,
          action: currentStep.action,
          timestamp: new Date().toISOString(),
          user: user?.username || 'Current User',
          notes: formData.notes || formData.comment || '',
          data: dataToSave,
          status: 'completed',
          approvalStatus: formData.decision || APPROVAL_STATUS.APPROVED
        }
      ];
    }
    
    // Determine next step only if not editing
    let nextStepId = currentCase.currentStepId;
    let nextWorkflow = currentWorkflow;
    let caseStatus = currentCase.status;
    
    if (!isEditing) {
      nextStepId = currentStep.nextStep;
      
      // Check if completing CBS workflow
      if (currentWorkflow === 'cbs' && !nextStepId) {
        // Transition to CAP
        nextWorkflow = currentCase.riskLevel === 'high' || currentCase.riskLevel === 'Cao' 
          ? 'cap_high' : 'cap_medium';
        const capWorkflow = nextWorkflow === 'cap_high' ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;
        nextStepId = capWorkflow.steps[0].id;
      }
      
      // Check if completing CAP workflow
      if ((currentWorkflow === 'cap_high' || currentWorkflow === 'cap_medium') && !nextStepId) {
        caseStatus = 'completed';
      }
    }
    
    const updatedCase = {
      ...currentCase,
      currentWorkflow: nextWorkflow,
      currentStepId: nextStepId,
      status: caseStatus,
      updatedAt: new Date().toISOString(),
      history: updatedHistory,
      data: {
        ...currentCase.data,
        [currentStep.id]: dataToSave
      }
    };
    
    updateCase(updatedCase);
    setCurrentCase(updatedCase);
    
    // Move to next step only if not editing
    if (!isEditing) {
      setCurrentWorkflow(nextWorkflow);
      
      if (nextStepId) {
        const workflow = nextWorkflow === 'cbs' ? CBS_WORKFLOW :
                        nextWorkflow === 'cap_high' ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;
        const nextStep = workflow.steps.find(s => s.id === nextStepId);
        setCurrentStep(nextStep);
      } else if (caseStatus === 'completed') {
        // Workflow completed
        alert('🎉 Hoàn thành toàn bộ workflow! Case đã được đóng.');
      }
    } else {
      alert('✅ Đã cập nhật dữ liệu thành công!');
    }
    
    closeModal();
  };
  
  const renderStepButton = (step) => {
    if (!step.buttonLabel) return null;
    if (!canUserAccessStep(step, user?.role)) return null;
    
    const isCurrentStep = currentStep?.id === step.id;
    const isPastStep = currentCase?.history.some(h => h.stepId === step.id);
    const canExecute = canCurrentUserExecuteStep(step.id, currentCase);
    
    return (
      <button
        key={step.id}
        className={`btn ${isCurrentStep ? 'btn-primary' : isPastStep ? 'btn-success' : 'btn-outline-secondary'} mb-2`}
        onClick={() => openModal(step.action)}
        disabled={!isCurrentStep || !canExecute}
        title={!canExecute ? `Bước này được phân công cho ${getStepAssignedUserLabel(step.id, currentCase)}` : ''}
      >
        {isPastStep && <i className="bi bi-check-circle me-2"></i>}
        {step.buttonLabel}
      </button>
    );
  };

  const renderAttachmentSection = () => (
    <>
      <div className="mb-3 mt-3">
        <label className="form-label">Tài liệu đính kèm (Optional)</label>
        <div
          className={`upload-dropzone ${isDraggingAttachment ? 'dragging' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingAttachment(true);
          }}
          onDragLeave={() => setIsDraggingAttachment(false)}
          onDrop={handleAttachmentDrop}
        >
          <input
            id="step-attachment-input"
            type="file"
            className="d-none"
            multiple
            onChange={handleFileSelect}
          />
          <i className="bi bi-cloud-arrow-up fs-2 text-primary mb-2"></i>
          <div className="fw-semibold mb-1">Kéo thả file vào đây</div>
          <small className="text-muted d-block mb-2">Hỗ trợ nhiều file: PDF, Word, Excel, Ảnh, CSV...</small>
          <label htmlFor="step-attachment-input" className="btn btn-outline-primary btn-sm">
            <i className="bi bi-folder2-open me-1"></i>
            Chọn file từ máy
          </label>
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-3">
          <label className="form-label">Danh sách file đã chọn:</label>
          <ul className="list-group">
            {selectedFiles.map((file, index) => (
              <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <i className="bi bi-file-earmark me-2"></i>
                  {file.name}
                  <small className="text-muted ms-2">({formatFileSize(file.size)})</small>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={() => downloadAttachment(file)}
                    title="Tải file"
                  >
                    <i className="bi bi-download"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFile(index)}
                    title="Xóa"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
  
  const renderModalContent = () => {
    if (!currentStep) return null;
    
    switch (currentStep.action) {
      case 'input_data':
        return (
          <div>
            <div className="alert alert-info mb-3">
              <i className="bi bi-info-circle me-2"></i>
              Nhập các chỉ tiêu cảnh báo sớm cho khách hàng từ danh sách đã khai báo trong hệ thống
            </div>
            
            {indicatorInputs.map((input, index) => (
              <div key={index} className="border rounded p-3 mb-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Chỉ tiêu #{index + 1}</strong>
                  {indicatorInputs.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeIndicatorInput(index)}
                    >
                      <i className="bi bi-trash"></i> Xóa
                    </button>
                  )}
                </div>
                
                <div className="mb-2">
                  <label className="form-label">Chọn chỉ tiêu <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={input.indicatorId}
                    onChange={(e) => updateIndicatorInput(index, 'indicatorId', e.target.value)}
                    required
                  >
                    <option value="">-- Chọn chỉ tiêu --</option>
                    {activeIndicators.map(ind => (
                      <option key={ind.id} value={ind.id}>
                        {ind.name} ({ind.code})
                      </option>
                    ))}
                  </select>
                  {input.indicatorId && (
                    <small className="text-muted d-block mt-1">
                      {activeIndicators.find(ind => ind.id === input.indicatorId)?.description}
                    </small>
                  )}
                </div>
                
                <div className="mb-2">
                  <label className="form-label">Giá trị <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={input.value}
                    onChange={(e) => updateIndicatorInput(index, 'value', e.target.value)}
                    placeholder="Ví dụ: 15%, 500 triệu, Cao..."
                    required
                  />
                </div>
                
                <div className="mb-0">
                  <label className="form-label">Ghi chú</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={input.notes}
                    onChange={(e) => updateIndicatorInput(index, 'notes', e.target.value)}
                    placeholder="Ghi chú thêm về chỉ tiêu này..."
                  />
                </div>
              </div>
            ))}
            
            <button
              type="button"
              className="btn btn-outline-primary btn-sm mb-3"
              onClick={addIndicatorInput}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Thêm chỉ tiêu
            </button>
            
            <div className="mb-3">
              <label className="form-label">Ghi chú tổng quan</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Ghi chú chung về dữ liệu nhập..."
              />
            </div>

            {renderAttachmentSection()}
          </div>
        );
        
      case 'approve_cas':
      case 'review_cas':
      case 'review_cap_proposal':
      case 'review_cap_result':
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Quyết định <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={formData.decision || ''}
                onChange={(e) => setFormData({...formData, decision: e.target.value})}
                required
              >
                <option value="">-- Chọn --</option>
                {currentStep.options?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {currentStep.requiresComment && (
              <div className="mb-3">
                <label className="form-label">Nhận xét <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={formData.comment || ''}
                  onChange={(e) => setFormData({...formData, comment: e.target.value})}
                  placeholder="Nhập nhận xét chi tiết..."
                  required
                />
              </div>
            )}

            {renderAttachmentSection()}
          </div>
        );
        
      case 'approve_cas_result':
      case 'approve_cap_proposal':
      case 'approve_cap_result':
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Phê duyệt <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={formData.decision || ''}
                onChange={(e) => setFormData({...formData, decision: e.target.value})}
                required
              >
                <option value="">-- Chọn --</option>
                <option value="Phê duyệt">Phê duyệt</option>
                <option value="Từ chối">Từ chối</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Ý kiến phê duyệt <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows="4"
                value={formData.comment || ''}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                placeholder="Nhập ý kiến..."
                required
              />
            </div>

            {renderAttachmentSection()}
          </div>
        );
        
      case 'select_cap_options':
        return (
          <div>
            <div className="mb-4">
              <label className="form-label fw-bold">Chọn phương án CAP <span className="text-danger">*</span></label>
              <div className="border rounded p-3">
                {CAP_OPTIONS.map(option => (
                  <div key={option.id} className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={option.id}
                      checked={formData.cap_options?.includes(option.id) || false}
                      onChange={(e) => {
                        const current = formData.cap_options || [];
                        const updated = e.target.checked 
                          ? [...current, option.id]
                          : current.filter(id => id !== option.id);
                        setFormData({...formData, cap_options: updated});
                      }}
                    />
                    <label className="form-check-label" htmlFor={option.id}>
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Ban XLN <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={formData.ban_xln || ''}
                  onChange={(e) => setFormData({...formData, ban_xln: e.target.value})}
                  required
                >
                  <option value="">-- Chọn --</option>
                  {CAP_CLASSIFICATION.ban_xln.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Tháp <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={formData.thap_level || ''}
                  onChange={(e) => setFormData({...formData, thap_level: e.target.value})}
                  required
                >
                  <option value="">-- Chọn --</option>
                  {CAP_CLASSIFICATION.thap.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {renderAttachmentSection()}
          </div>
        );
        
      case 'implement_cap':
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Kết quả thực hiện <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows="4"
                value={formData.comment || ''}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                placeholder="Mô tả chi tiết kết quả triển khai..."
                required
              />
            </div>
            
            {renderAttachmentSection()}
          </div>
        );
        
      case 'close_case':
        return (
          <div>
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Xác nhận đóng trường hợp</strong>
              <p className="mb-0 mt-2">Sau khi đóng, case này sẽ được chuyển sang trạng thái hoàn thành.</p>
            </div>
            <div className="mb-3">
              <label className="form-label">Ghi chú đóng case <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows="4"
                value={formData.comment || ''}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                placeholder="Nhập lý do và kết quả xử lý..."
                required
              />
            </div>

            {renderAttachmentSection()}
          </div>
        );
        
      default:
        return (
          <div className="mb-3">
            <label className="form-label">Ghi chú</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.notes || ''}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Nhập ghi chú..."
            />

            {renderAttachmentSection()}
          </div>
        );
    }
  };
  
  const getWorkflowSteps = () => {
    if (currentWorkflow === 'cbs') return CBS_WORKFLOW.steps;
    if (currentWorkflow === 'cap_high') return CAP_HIGH_WORKFLOW.steps;
    if (currentWorkflow === 'cap_medium') return CAP_MEDIUM_WORKFLOW.steps;
    return [];
  };
  
  // Get risk level badge info
  const getRiskBadge = (level) => {
    if (level === 'high' || level === 'Cao') return 'danger';
    if (level === 'medium' || level === 'Trung bình') return 'warning';
    return 'info';
  };

  const hasMeaningfulData = (data) => {
    if (!data || typeof data !== 'object') return false;
    return Object.values(data).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.keys(value).length > 0;
      return value !== '' && value !== null && value !== undefined;
    });
  };
  
  // Get workflow progress
  const getWorkflowProgress = (caseItem) => {
    const completedSteps = new Set(caseItem.history.map(h => h.stepId)).size;
    const workflow = caseItem.currentWorkflow === 'cbs' ? CBS_WORKFLOW :
                    caseItem.riskLevel === 'high' || caseItem.riskLevel === 'Cao'
                    ? CAP_HIGH_WORKFLOW : CAP_MEDIUM_WORKFLOW;

    const totalSteps = workflow.steps.length;
    return { completed: completedSteps, total: totalSteps, percent: Math.round((completedSteps / totalSteps) * 100) };
  };
  
  // Filter cases by branch, hidden status, and search term
  const filteredCases = cases.filter(c => {
    // Filter out closed and hidden cases
    if (c.status === 'closed' || c.isHidden === true) return false;
    
    // Get customer for branch filtering
    const customer = customers.find(cust => cust.id === c.customerId);
    
    // Filter by branch if user has limited access
    // IMPORTANT: If customer not found (e.g., auto-created cases), still show the case
    if (customer) {
      const accessibleCustomers = filterCustomersByBranch(customers);
      const hasAccess = accessibleCustomers.some(ac => ac.id === customer.id);
      if (!hasAccess) {
        console.log(`⚠️ Case ${c.id} filtered out - customer ${c.customerId} not accessible`);
        return false;
      }
    } else {
      console.log(`⚠️ Case ${c.id}: Customer ${c.customerId} not found in customers array - will still show`);
    }
    
    // Filter by case access control
    if (!canCurrentUserViewCaseByAccessControl(c)) {
      return false;
    }

    // Filter by search term
    const matchSearch = !searchTerm || (
      c.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.customerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.warningCode && c.warningCode.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    return matchSearch;
  }).sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return dateB - dateA;
  });
  
  // Debug log
  console.log(`📊 CustomerCaseManagement: Total cases: ${cases.length}, Filtered: ${filteredCases.length}`);
  
  // Handle hide/show case
  const handleToggleCaseVisibility = (caseItem) => {
    if (window.confirm(`Bạn có chắc muốn ${caseItem.isHidden ? 'hiển thị' : 'ẩn'} case này khỏi danh sách?`)) {
      updateCase({ ...caseItem, isHidden: !caseItem.isHidden });
    }
  };
  
  // Handle delete case
  const handleDeleteCase = (caseItem) => {
    if (window.confirm(`Bạn có chắc muốn XÓA case ${caseItem.id} của ${caseItem.customerName}?\n\nLưu ý: Hành động này KHÔNG THỂ HOÀN TÁC!`)) {
      deleteCase(caseItem.id);
      // If current case is deleted, clear selection
      if (currentCase?.id === caseItem.id) {
        setCurrentCase(null);
        setSelectedCustomer(null);
      }
    }
  };
  
  // Cleanup duplicate cases - keep only the newest case per customer
  const handleCleanupDuplicates = () => {
    // Group cases by customerId
    const casesByCustomer = {};
    cases.filter(c => c.status !== 'closed').forEach(c => {
      if (!casesByCustomer[c.customerId]) {
        casesByCustomer[c.customerId] = [];
      }
      casesByCustomer[c.customerId].push(c);
    });
    
    // Find customers with multiple cases
    const duplicates = Object.entries(casesByCustomer).filter(([_, cases]) => cases.length > 1);
    
    if (duplicates.length === 0) {
      alert('✅ Không có cases trùng lặp!');
      return;
    }
    
    const totalToDelete = duplicates.reduce((sum, [_, cases]) => sum + cases.length - 1, 0);
    
    if (!window.confirm(`Tìm thấy ${duplicates.length} khách hàng có nhiều cases (tổng ${totalToDelete} cases trùng).\n\nXóa các cases cũ và giữ lại case mới nhất cho mỗi khách hàng?`)) {
      return;
    }
    
    let deletedCount = 0;
    duplicates.forEach(([customerId, customerCases]) => {
      // Sort by createdAt descending (newest first)
      const sorted = [...customerCases].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      // Keep the first (newest), delete the rest
      const toDelete = sorted.slice(1);
      toDelete.forEach(c => {
        deleteCase(c.id);
        deletedCount++;
      });
    });
    
    // Clear selection if current case was deleted
    if (currentCase && !cases.find(c => c.id === currentCase.id)) {
      setCurrentCase(null);
      setSelectedCustomer(null);
    }
    
    alert(`✅ Đã xóa ${deletedCount} cases trùng lặp!\n\nGiữ lại ${duplicates.length} cases (mới nhất cho mỗi khách hàng)`);
  };
  
  return (
    <div className="container-fluid p-4">
      <div className="page-header-sticky">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-diagram-3 me-2"></i>
            Quản lý Case - CBS & CAP Workflow
          </h2>
          <p className="text-muted mb-0">Quản lý quy trình xử lý khách hàng từ điều tra đến triển khai giải pháp</p>
        </div>
      </div>
      
      <div className="row">
        {/* Case List */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">
                <i className="bi bi-folder2-open me-2"></i>
                Danh sách Cases
              </h5>
              <small>Tổng: {filteredCases.length} case đang xử lý</small>
            </div>
            <div className="card-body">
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Tìm case, khách hàng, cảnh báo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              
              <button 
                className="btn btn-sm btn-outline-warning w-100 mb-3"
                onClick={handleCleanupDuplicates}
                title="Tự động xóa các cases trùng lặp của cùng 1 khách hàng (giữ lại case mới nhất)"
              >
                <i className="bi bi-trash3 me-1"></i>
                Dọn dẹp Cases trùng
              </button>
              
              {filteredCases.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                  <p>Chưa có case nào được tạo</p>
                  <small>Tạo case từ trang Cảnh báo sớm</small>
                </div>
              ) : (
                <div style={{maxHeight: '600px', overflowY: 'auto'}}>
                  {filteredCases.map(caseItem => {
                    const progress = getWorkflowProgress(caseItem);
                    const isSelected = currentCase?.id === caseItem.id;
                    
                    return (
                      <div
                        key={caseItem.id}
                        className={`card mb-3 cursor-pointer hover-lift ${isSelected ? 'border-danger border-2' : ''}`}
                        onClick={() => handleSelectCase(caseItem)}
                        style={{cursor: 'pointer'}}
                      >
                        <div className="card-body p-3">
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <small className="text-muted d-block">{caseItem.id}</small>
                              <strong className="d-block">{caseItem.customerName}</strong>
                              <small className="text-muted">{caseItem.customerId}</small>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-1">
                              <span className={`badge bg-${getRiskBadge(caseItem.warningLevel || caseItem.riskLevel)}`}>
                                {caseItem.warningLevel === 'high' || caseItem.riskLevel === 'high' || caseItem.riskLevel === 'Cao' ? 'Cao' :
                                 caseItem.warningLevel === 'medium' || caseItem.riskLevel === 'medium' || caseItem.riskLevel === 'Trung bình' ? 'TB' : 'Thấp'}
                              </span>
                              {caseItem.isManual && (
                                <span className="badge bg-info">Manual</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Warning Info */}
                          {caseItem.warningCode && (
                            <div className="mb-2">
                              <small className="text-muted">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-1"></i>
                                {caseItem.warningCode}
                              </small>
                              {caseItem.warningScore && (
                                <small className="text-danger ms-2">
                                  Điểm: {caseItem.warningScore}
                                </small>
                              )}
                            </div>
                          )}
                          
                          {/* Workflow Status */}
                          <div className="mb-2">
                            <small className="text-muted d-block mb-1">
                              {caseItem.currentWorkflow === 'cbs' ? '🔵 CBS Workflow' :
                               caseItem.currentWorkflow === 'cap_high' ? '🔴 CAP High Risk' :
                               '🟡 CAP Medium/Low'}
                            </small>
                            <div className="progress" style={{height: '6px'}}>
                              <div 
                                className={`progress-bar bg-${caseItem.currentWorkflow === 'cbs' ? 'primary' : 'danger'}`}
                                style={{width: `${progress.percent}%`}}
                              ></div>
                            </div>
                            <small className="text-muted">{progress.completed}/{progress.total} bước</small>
                          </div>

                          {/* Access Scope Badges */}
                          <div className="mb-2 d-flex flex-wrap gap-1">
                            {(caseItem.accessControl?.branches || ['all']).includes('all') ? (
                              <span className="badge bg-light text-dark border">🌐 CN: Tất cả</span>
                            ) : (
                              <span className="badge bg-light text-dark border">🏢 CN: {(caseItem.accessControl?.branches || []).length}</span>
                            )}

                            {(caseItem.accessControl?.groups || ['all']).includes('all') ? (
                              <span className="badge bg-light text-dark border">👥 Nhóm: Tất cả</span>
                            ) : (
                              <span className="badge bg-light text-dark border">👥 Nhóm: {(caseItem.accessControl?.groups || []).length}</span>
                            )}

                            {(caseItem.accessControl?.users || ['all']).includes('all') ? (
                              <span className="badge bg-light text-dark border">👤 User: Tất cả</span>
                            ) : (
                              <span className="badge bg-light text-dark border">👤 User: {(caseItem.accessControl?.users || []).length}</span>
                            )}
                          </div>
                          
                          {/* Timestamp */}
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {new Date(caseItem.updatedAt).toLocaleDateString('vi-VN')}
                            </small>
                            <div className="d-flex gap-2">
                              {caseItem.isManual && (
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleCaseVisibility(caseItem);
                                  }}
                                  title="Ẩn case khỏi danh sách"
                                >
                                  <i className="bi bi-eye-slash"></i>
                                </button>
                              )}
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCase(caseItem);
                                }}
                                title="Xóa case vĩnh viễn"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Workflow Area */}
        <div className="col-md-8">
          {!currentCase ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="bi bi-arrow-left-circle fs-1 text-muted"></i>
                <p className="text-muted mt-3">Vui lòng chọn case để xem chi tiết</p>
                <small className="text-muted">Hoặc tạo case mới từ trang Cảnh báo sớm</small>
              </div>
            </div>
          ) : (
            <>
              {/* Case & Customer Info */}
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{selectedCustomer?.name || currentCase.customerName}</h5>
                    <span className="badge bg-info">{currentCase.id}</span>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-3">Thông tin khách hàng</h6>
                      <p className="mb-1"><strong>Mã KH:</strong> {currentCase.customerId}</p>
                      <p className="mb-1"><strong>Loại KH:</strong> {currentCase.customerType || 'Cá nhân'}</p>
                      <p className="mb-1">
                        <strong>Mức độ rủi ro:</strong>{' '}
                        <span className={`badge bg-${getRiskBadge(currentCase.riskLevel)}`}>
                          {currentCase.riskLevel === 'high' || currentCase.riskLevel === 'Cao' ? 'Cao' :
                           currentCase.riskLevel === 'medium' || currentCase.riskLevel === 'Trung bình' ? 'Trung bình' : 'Thấp'}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-3">Thông tin cảnh báo</h6>
                      {currentCase.warningCode ? (
                        <>
                          <p className="mb-1">
                            <strong>Mã cảnh báo:</strong>{' '}
                            <span className="badge bg-warning text-dark">{currentCase.warningCode}</span>
                          </p>
                          <p className="mb-1">
                            <strong>Loại cảnh báo:</strong> {currentCase.alertType || '-'}
                          </p>
                          <p className="mb-1">
                            <strong>Điểm rủi ro:</strong>{' '}
                            <span className="text-danger fw-bold">{currentCase.warningScore || '-'}</span>
                          </p>
                          {currentCase.data?.warningData?.model && (
                            <p className="mb-1">
                              <strong>Mô hình:</strong> {currentCase.data.warningData.model}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-muted">Case không liên kết với cảnh báo</p>
                      )}
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-12">
                      <h6 className="text-muted mb-2">Thông tin người tạo case</h6>
                      <div className="p-2 border rounded bg-light-subtle">
                        <div className="d-flex flex-wrap gap-3">
                          <div><strong>Người tạo:</strong> {currentCase.createdBy?.name || '-'}</div>
                          <div><strong>Email:</strong> {currentCase.createdBy?.email || '-'}</div>
                          <div><strong>Nhóm:</strong> {currentCase.createdBy?.group || '-'}</div>
                          <div><strong>Thời gian tạo:</strong> {currentCase.createdAt ? new Date(currentCase.createdAt).toLocaleString('vi-VN') : '-'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  {(() => {
                    const progress = getWorkflowProgress(currentCase);
                    return (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Tiến độ xử lý:</strong>
                          <span className="badge bg-secondary">{progress.completed}/{progress.total} bước ({progress.percent}%)</span>
                        </div>
                        <div className="progress" style={{height: '20px'}}>
                          <div 
                            className={`progress-bar progress-bar-striped ${currentCase.status === 'completed' ? 'bg-success' : currentCase.currentWorkflow === 'cbs' ? 'bg-primary' : 'bg-danger'}`}
                            style={{width: `${progress.percent}%`}}
                          >
                            {progress.percent}%
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Status Alert */}
                  {currentCase.status === 'completed' ? (
                    <div className="alert alert-success mt-3 mb-0">
                      <i className="bi bi-check-circle-fill me-2"></i>
                      🎉 Hoàn thành toàn bộ workflow! Case đã được đóng.
                    </div>
                  ) : currentWorkflow === 'cbs' ? (
                    <div className="alert alert-info mt-3 mb-0">
                      <strong>Trạng thái:</strong> Đang thực hiện điều tra CBS (bước {currentCase.history.filter(h => h.status === 'completed').length}/{CBS_WORKFLOW.steps.length})
                      <br/>
                      <small>Sau khi hoàn thành CBS sẽ chuyển sang CAP Workflow</small>
                    </div>
                  ) : (
                    <div className={`alert alert-${currentCase.currentWorkflow === 'cap_high' ? 'danger' : 'warning'} mt-3 mb-0`}>
                      <strong>Trạng thái:</strong> Đang thực hiện biện pháp CAP 
                      {currentCase.currentWorkflow === 'cap_high' ? ' (Rủi ro Cao - 7 bước)' : ' (Rủi ro TB/Thấp - 3 bước)'}
                      <br/>
                      <small>Hoàn thành CAP để đóng case</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-shield-lock me-2"></i>
                    Phân quyền xem dữ liệu Case
                  </h5>
                  <small className="text-muted">All + Search cho Chi nhánh/Nhóm/User</small>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Chi nhánh</label>
                      <Select
                        isMulti
                        isSearchable
                        options={branchOptions}
                        value={branchOptions.filter(opt => (accessControl.branches || ['all']).includes(opt.value))}
                        onChange={(selected) => handleSelectWithAll(selected, 'branches')}
                        placeholder="Chọn chi nhánh..."
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Nhóm người dùng</label>
                      <Select
                        isMulti
                        isSearchable
                        options={groupOptions}
                        value={groupOptions.filter(opt => (accessControl.groups || ['all']).includes(opt.value))}
                        onChange={(selected) => handleSelectWithAll(selected, 'groups')}
                        placeholder="Chọn nhóm người dùng..."
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Người dùng</label>
                      <Select
                        isMulti
                        isSearchable
                        options={userOptions}
                        value={userOptions.filter(opt => (accessControl.users || ['all']).includes(opt.value))}
                        onChange={(selected) => handleSelectWithAll(selected, 'users')}
                        placeholder="Search người dùng theo Branch + Group..."
                      />
                    </div>
                  </div>

                  <div className="mt-3 p-2 rounded border bg-light">
                    <small className="text-muted">
                      Rule: Nếu chọn chi nhánh cụ thể thì chỉ user thuộc nhóm + chi nhánh tương ứng được phép xem case này.
                    </small>
                  </div>

                  <div className="mt-3 text-end">
                    <button type="button" className="btn btn-primary" onClick={saveCaseAccessControl}>
                      <i className="bi bi-save me-2"></i>
                      Lưu phân quyền
                    </button>
                  </div>
                </div>
              </div>

              {/* Case Documents - optional, upload anytime */}
              <div className="card mb-3">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-paperclip me-2"></i>
                    Tài liệu Case (Optional)
                  </h5>
                  <small className="text-muted">Upload nhiều file bất kỳ lúc nào</small>
                </div>
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-5">
                      <label className="form-label">Ghi chú tài liệu (tuỳ chọn)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={documentNote}
                        onChange={(e) => setDocumentNote(e.target.value)}
                        placeholder="Ví dụ: Biên bản họp, chứng từ bổ sung..."
                      />
                    </div>
                    <div className="col-md-7">
                      <label className="form-label">Chọn tài liệu</label>
                      <div
                        className={`upload-dropzone ${isDraggingCaseDocs ? 'dragging' : ''}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingCaseDocs(true);
                        }}
                        onDragLeave={() => setIsDraggingCaseDocs(false)}
                        onDrop={handleCaseDocumentDrop}
                      >
                        <input
                          id="case-documents-input"
                          type="file"
                          className="d-none"
                          multiple
                          onChange={handleCaseDocumentUpload}
                        />
                        <i className="bi bi-cloud-arrow-up fs-2 text-primary mb-2"></i>
                        <div className="fw-semibold mb-1">Kéo thả tài liệu case vào đây</div>
                        <small className="text-muted d-block mb-2">Upload nhiều file bất kỳ lúc nào</small>
                        <label htmlFor="case-documents-input" className="btn btn-outline-primary btn-sm">
                          <i className="bi bi-folder2-open me-1"></i>
                          Chọn tài liệu
                        </label>
                      </div>
                    </div>
                  </div>

                  {(currentCase.data?.documents || []).length > 0 ? (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Tài liệu</th>
                            <th>Ghi chú</th>
                            <th>Người upload</th>
                            <th>Thời gian</th>
                            <th className="text-end">Hành động</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(currentCase.data?.documents || []).map((doc) => (
                            <tr key={doc.id}>
                              <td>
                                <i className="bi bi-file-earmark me-2"></i>
                                {doc.name}
                                <small className="text-muted ms-2">({formatFileSize(doc.size)})</small>
                              </td>
                              <td>{doc.note || '-'}</td>
                              <td>{doc.uploadedBy || '-'}</td>
                              <td><small>{new Date(doc.uploadDate).toLocaleString('vi-VN')}</small></td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => downloadAttachment(doc)}
                                    title="Tải file"
                                  >
                                    <i className="bi bi-download"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeCaseDocument(doc.id)}
                                    title="Xóa"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted mt-3 mb-0">
                      <small>Chưa có tài liệu nào được upload cho case này.</small>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Workflow Progress - Horizontal Stepper */}
              <div className="card mb-3">
                <div className="card-header bg-white">
                  <h5 className="mb-0">
                    {currentWorkflow === 'cbs' ? '🔵 CBS Workflow - Điều tra khách hàng' : 
                     currentWorkflow === 'cap_high' ? '🔴 CAP Workflow - Rủi ro cao' : 
                     '🟡 CAP Workflow - Rủi ro thấp'}
                  </h5>
                </div>
                <div className="card-body">
                  {/* Horizontal step bar */}
                  <div className="workflow-hsteps">
                    {getWorkflowSteps().map((step, index, arr) => {
                      const isCompleted = currentCase?.history.some(h => h.stepId === step.id);
                      const isCurrent = currentStep?.id === step.id;
                      return (
                        <React.Fragment key={step.id}>
                          <div
                            className={`hstep ${isCompleted ? 'hstep--done' : isCurrent ? 'hstep--active' : 'hstep--pending'}`}
                            onClick={() => isCurrent ? null : isCompleted && setCurrentStep(step)}
                            title={step.name}
                          >
                            <div className="hstep__circle">
                              {isCompleted
                                ? <i className="bi bi-check-lg"></i>
                                : <span>{step.order}</span>}
                            </div>
                            <div className="hstep__label">{step.name}</div>
                          </div>
                          {index < arr.length - 1 && (
                            <div className={`hstep__connector ${isCompleted ? 'hstep__connector--done' : ''}`}></div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Active / selected step detail panel */}
                  {currentStep && (() => {
                    const step = currentStep;
                    const isCompleted = currentCase?.history.some(h => h.stepId === step.id);
                    const historyItem = currentCase?.history.find(h => h.stepId === step.id);
                    const hasData = hasMeaningfulData(historyItem?.data) || Boolean(historyItem?.notes);
                    const canExecute = canCurrentUserExecuteStep(step.id, currentCase);

                    return (
                      <div className="hstep__detail mt-4 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-0 fw-bold">
                              {isCompleted && <i className="bi bi-check-circle-fill text-success me-2"></i>}
                              Bước {step.order}: {step.name}
                            </h6>
                            <small className="text-muted">{step.description}</small>
                          </div>
                          {isCompleted && historyItem && (
                            <small className="text-muted text-nowrap ms-3">
                              <i className="bi bi-clock me-1"></i>
                              {new Date(historyItem.timestamp).toLocaleString('vi-VN')}
                            </small>
                          )}
                        </div>

                        {/* Assignee */}
                        <div className="mb-3 p-2 border rounded bg-light-subtle">
                          <small className="text-muted d-block mb-1">
                            <i className="bi bi-person-check me-1"></i>
                            Người thực hiện bước này
                          </small>
                          {step.id === 'cbs_1' ? (
                            <span className="badge bg-primary-subtle text-primary-emphasis border">
                              👤 {currentCase?.createdBy?.name || getStepAssignedUserLabel(step.id, currentCase)} (User tạo case)
                            </span>
                          ) : (
                            <Select
                              isSearchable
                              isClearable={false}
                              options={stepExecutionOptions}
                              value={stepExecutionOptions.find(opt => opt.value === getStepAssignedUserValue(step.id, currentCase)) || stepExecutionOptions[0]}
                              onChange={(selected) => updateStepAssignment(step.id, selected?.value)}
                              placeholder="Chọn người thực hiện..."
                            />
                          )}
                          <small className="text-muted">
                            Nếu không chọn cụ thể, mặc định: tất cả user đã phân quyền case.
                          </small>
                        </div>

                        {/* Action buttons */}
                        <div className="d-flex gap-2 flex-wrap">
                          {canUserAccessStep(step, user?.role) ? (
                            renderStepButton(step)
                          ) : (
                            <small className="text-muted">
                              <i className="bi bi-lock me-1"></i>
                              Yêu cầu quyền: {step.requiredRole?.join(', ')}
                            </small>
                          )}
                          {isCompleted && hasData && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => viewStepData(step.id)}
                              >
                                <i className="bi bi-eye me-1"></i>
                                Xem chi tiết
                              </button>
                              {canUserAccessStep(step, user?.role) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => editStepData(step.id)}
                                >
                                  <i className="bi bi-pencil me-1"></i>
                                  Sửa
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        {!canExecute && (
                          <div className="mt-2">
                            <small className="text-warning">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Bước này được phân công cho {getStepAssignedUserLabel(step.id, currentCase)}
                            </small>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* History */}
              {currentCase && currentCase.history.length > 1 && (
                <div className="card">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">
                      <i className="bi bi-clock-history me-2"></i>
                      Lịch sử xử lý
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Thời gian</th>
                            <th>Bước</th>
                            <th>Người xử lý</th>
                            <th>Ghi chú</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentCase.history.slice().reverse().map((item, index) => (
                            <tr key={index}>
                              <td><small>{new Date(item.timestamp).toLocaleString('vi-VN')}</small></td>
                              <td><small>{item.stepId}</small></td>
                              <td><small>{item.user}</small></td>
                              <td><small>{item.notes || '-'}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {currentStep?.name}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <form onSubmit={handleSubmitAction}>
                <div className="modal-body">
                  {renderModalContent()}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Hủy
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-check-circle me-2"></i>
                    Xác nhận
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* View Step Data Modal */}
      {viewingStepData && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-eye me-2"></i>
                  Chi tiết dữ liệu đã nhập - {viewingStepData.stepId}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeViewStepData}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>Thời gian:</strong> {new Date(viewingStepData.timestamp).toLocaleString('vi-VN')}
                </div>
                <div className="mb-3">
                  <strong>Người xử lý:</strong> {viewingStepData.user}
                </div>
                
                {viewingStepData.data?.indicators && (
                  <div className="mb-3">
                    <strong className="d-block mb-2">Chỉ tiêu đã nhập:</strong>
                    {viewingStepData.data.indicators.map((ind, index) => {
                      const indicator = activeIndicators.find(i => i.id === ind.indicatorId);
                      return (
                        <div key={index} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="row">
                              <div className="col-md-5">
                                <small className="text-muted">Chỉ tiêu</small>
                                <div><strong>{indicator?.name || ind.indicatorId}</strong></div>
                                {indicator && <small className="text-muted">{indicator.code}</small>}
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted">Giá trị</small>
                                <div><strong className="text-primary">{ind.value}</strong></div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Ghi chú</small>
                                <div>{ind.notes || '-'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {viewingStepData.data?.cap_options && (
                  <div className="mb-3">
                    <strong className="d-block mb-2">Phương án CAP đã chọn:</strong>
                    <ul>
                      {viewingStepData.data.cap_options.map(optId => {
                        const option = CAP_OPTIONS.find(o => o.id === optId);
                        return <li key={optId}>{option?.label || optId}</li>;
                      })}
                    </ul>
                  </div>
                )}
                
                {viewingStepData.data?.ban_xln && (
                  <div className="mb-2">
                    <strong>Ban XLN:</strong> {viewingStepData.data.ban_xln}
                  </div>
                )}
                
                {viewingStepData.data?.thap_level && (
                  <div className="mb-2">
                    <strong>Tháp:</strong> {viewingStepData.data.thap_level}
                  </div>
                )}
                
                {viewingStepData.data?.decision && (
                  <div className="mb-3">
                    <strong>Quyết định:</strong>{' '}
                    <span className={`badge bg-${viewingStepData.data.decision === 'approved' || viewingStepData.data.decision === 'agree' ? 'success' : 'danger'}`}>
                      {viewingStepData.data.decision}
                    </span>
                  </div>
                )}
                
                {viewingStepData.data?.files && viewingStepData.data.files.length > 0 && (
                  <div className="mb-3">
                    <strong className="d-block mb-2">File đính kèm:</strong>
                    <ul className="list-unstyled">
                      {viewingStepData.data.files.map((file, index) => (
                        <li key={index} className="mb-2 d-flex justify-content-between align-items-center border rounded px-2 py-1">
                          <div>
                            <i className="bi bi-file-earmark me-2"></i>
                            {file.name} <small className="text-muted">({formatFileSize(file.size)})</small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => downloadAttachment(file)}
                            title="Tải file"
                          >
                            <i className="bi bi-download"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {viewingStepData.notes && (
                  <div className="mb-2">
                    <strong>Ghi chú:</strong>
                    <div className="alert alert-secondary mt-2">{viewingStepData.notes}</div>
                  </div>
                )}

                {(() => {
                  const excludedKeys = ['indicators', 'cap_options', 'files', 'ban_xln', 'thap_level', 'decision'];
                  const genericEntries = Object.entries(viewingStepData.data || {}).filter(([key, value]) => {
                    if (excludedKeys.includes(key)) return false;
                    if (value === '' || value === null || value === undefined) return false;
                    if (Array.isArray(value) && value.length === 0) return false;
                    return true;
                  });

                  if (genericEntries.length === 0) return null;

                  return (
                    <div className="mb-2">
                      <strong className="d-block mb-2">Thông tin khác:</strong>
                      <ul className="mb-0">
                        {genericEntries.map(([key, value]) => (
                          <li key={key}>
                            <strong>{key}:</strong> {Array.isArray(value) ? value.join(', ') : String(value)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeViewStepData}>
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        /* ===================== Horizontal Stepper ===================== */
        .workflow-hsteps {
          display: flex;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 0.5rem;
          gap: 0;
        }
        .hstep {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          min-width: 90px;
          max-width: 110px;
          cursor: default;
          flex-shrink: 0;
        }
        .hstep--done {
          cursor: pointer;
        }
        .hstep__circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.2s ease;
        }
        .hstep--done   .hstep__circle { background: #28a745; color: #fff; }
        .hstep--active .hstep__circle { background: #0d6efd; color: #fff; box-shadow: 0 0 0 4px rgba(13,110,253,.25); }
        .hstep--pending .hstep__circle { background: #dee2e6; color: #6c757d; }
        .hstep__label {
          margin-top: 6px;
          font-size: 0.72rem;
          line-height: 1.3;
          color: #495057;
          word-break: break-word;
        }
        .hstep--active .hstep__label  { font-weight: 700; color: #0d6efd; }
        .hstep--done   .hstep__label  { color: #28a745; }
        .hstep--pending .hstep__label { color: #adb5bd; }
        .hstep__connector {
          flex: 1;
          height: 3px;
          background: #dee2e6;
          align-self: center;
          min-width: 16px;
          margin-top: -28px;   /* align with circle vertical centre */
        }
        .hstep__connector--done { background: #28a745; }
        .hstep__detail {
          background: #f8f9fa;
        }
        /* ===================== Cards / hover ========================== */
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        .upload-dropzone {
          border: 2px dashed #b6ccff;
          border-radius: 12px;
          background: linear-gradient(180deg, #f8fbff 0%, #f3f8ff 100%);
          min-height: 150px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1rem;
          transition: all 0.2s ease;
        }
        .upload-dropzone.dragging {
          border-color: #2f6fec;
          background: #eaf2ff;
          transform: scale(1.01);
        }
      `}</style>
    </div>
  );
}

export default CustomerCaseManagement;
